import React, { useState } from 'react';

import ClearIcon from '@mui/icons-material/Clear';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { alpha, styled } from '@mui/material/styles';

import { useFieldDescription } from './fieldDescription';
import { sharedClasses } from './sharedClasses';
import { DARK_GREY, WHITE } from '../../../ThemeContext/constants';
import { theme } from '../../../ThemeContext/ThemeObject';

import type { SelectChangeEvent } from '@mui/material/Select';

export interface OptionBase {
  value: string;
  // React 19's ReactNode includes Promise, which makes `renderValue` look like
  // it can return a promise. ESLint then auto-fixes it to `async` and the
  // Select never paints (async Client Component). Keep this a sync node type.
  label: string | React.ReactElement;
}

/**
 * Normalizes a plain string array into OptionBase[] (value === label for each entry).
 * Use this to pass string options into FormSelectField, e.g. options={toOptions(['Yes', 'No'])}.
 */
export const toOptions = (strings: string[]): OptionBase[] =>
  strings.map((value) => ({ value, label: value }));

interface IFormSelectFieldProps<T extends OptionBase = OptionBase> {
  label?: string;
  // Plain string arrays are accepted for backward compatibility and normalized to
  // OptionBase[] internally (value === label). Typed object arrays flow through as-is.
  options?: T[] | string[];
  defaultValue?: string;
  onChange?: (value: string) => void;
  id?: string;
  innerRef?: React.RefObject<HTMLInputElement | null> | ((el: HTMLDivElement) => void);
  styles?: React.CSSProperties | Record<string, React.CSSProperties>;
  error?: string;
  required?: boolean;
  isDisabled?: boolean;
  clearable?: boolean;
  placeholder?: string;
  placeholderColor?: string;
  fullWidth?: boolean;
  /** Optional per-option adornment rendered after the label (e.g. a badge Chip or a color dot). */
  renderAdornment?: (option: T) => React.ReactNode;
}

const SelectInput = styled(InputBase)<{ error?: boolean }>(({ error }) => ({
  'label + &': {
    marginTop: '24px'
  },
  '& .MuiInputBase-input': {
    borderRadius: '6px',
    position: 'relative',
    backgroundColor: WHITE,
    color: '#333333',
    border: `1px solid ${error ? '#eca8a6' : '#DDDDDD'}`,
    fontSize: '16px',
    padding: '6px 10px',
    margin: '3px',
    fontFamily: 'Source Sans Pro, sans-serif',
    '&:focus': {
      boxShadow: error
        ? `${alpha('#E37D7A', 0.25)} 0 0 0 2px`
        : `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 2px`,
      borderColor: error ? '#E37D7A' : theme.palette.primary.main
    }
  },
  '& .MuiInputBase-input:focus': {
    backgroundColor: WHITE,
    borderRadius: '6px'
  },
  // Scoped to the dropdown arrow only (not .MuiSvgIcon-root) so option adornment
  // icons shown in the collapsed input keep their own colors.
  '& .MuiSelect-icon': {
    color: '#AAAAAA',
    marginRight: '10px',
    transition: 'all 0.16s ease-in-out'
  }
}));

const menuItemStyle = {
  fontFamily: 'Source Sans Pro, sans-serif',
  color: '#333333',
  maxHeight: '200px'
};

const FormSelectField = <T extends OptionBase = OptionBase>({
  label,
  options,
  defaultValue,
  onChange,
  id,
  innerRef,
  styles,
  error,
  required,
  isDisabled = false,
  clearable = false,
  placeholder,
  placeholderColor = '#999999',
  fullWidth = false,
  renderAdornment
}: IFormSelectFieldProps<T>): React.JSX.Element => {
  const field = useFieldDescription(id, error);
  const [value, setValue] = useState(defaultValue ?? '');
  const [trackedDefault, setTrackedDefault] = useState(defaultValue);
  if (defaultValue !== trackedDefault) {
    setTrackedDefault(defaultValue);
    setValue(defaultValue ?? '');
  }
  // Coerce legacy string[] options into OptionBase[] (value === label) so callers
  // that predate the generic refactor keep rendering. Typed options pass through.
  const selectOptions: T[] = (options ?? []).map((option) =>
    typeof option === 'string' ? ({ value: option, label: option } as T) : option
  );

  const handleChange = (event: SelectChangeEvent) => {
    setValue(event.target.value);
    onChange?.(event.target.value);
  };

  const handleClear = () => {
    setValue('');
    onChange?.('');
  };

  return (
    <FormControl
      ref={innerRef}
      sx={{ position: 'relative', width: fullWidth ? '100%' : undefined }}
      variant="standard"
    >
      {label ? (
        <InputLabel
          shrink
          htmlFor={field.controlId}
          id={field.labelId}
          sx={sharedClasses.inputLabel}
        >
          {label}
          {required ? <span>*</span> : null}
        </InputLabel>
      ) : null}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          mt: label ? '24px' : 0
        }}
      >
        <Select
          disabled={isDisabled}
          displayEmpty={!!placeholder}
          id={field.controlId}
          input={<SelectInput error={!!error} />}
          labelId={label ? field.labelId : undefined}
          onChange={handleChange}
          sx={{ ...styles, ...(fullWidth ? { width: '100%' } : {}) }}
          value={value}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 500
              }
            }
          }}
          renderValue={(selected) => {
            if (!selected && placeholder) {
              return (
                <em
                  style={{
                    color: placeholderColor,
                    fontFamily: 'Source Sans Pro, sans-serif',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    lineHeight: '18px'
                  }}
                >
                  {placeholder}
                </em>
              );
            }
            const match = selectOptions.find((option) => option.value === selected);
            if (match) {
              return renderAdornment ? (
                <>
                  {match.label}
                  {renderAdornment(match)}
                </>
              ) : (
                match.label
              );
            }
            return selected;
          }}
          SelectDisplayProps={{
            'aria-describedby': field.describedBy,
            'aria-invalid': field.invalid ? true : undefined
          }}
        >
          {clearable && !required ? (
            <MenuItem sx={menuItemStyle} value="">
              {placeholder ? (
                <em
                  style={{
                    color: placeholderColor,
                    fontFamily: 'Source Sans Pro, sans-serif',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    lineHeight: '18px'
                  }}
                >
                  {placeholder}
                </em>
              ) : null}
            </MenuItem>
          ) : null}
          {selectOptions.map((option) => (
            <MenuItem key={option.value} sx={menuItemStyle} value={option.value}>
              {option.label}
              {renderAdornment ? renderAdornment(option) : null}
            </MenuItem>
          ))}
        </Select>
        {clearable && !required && value && !isDisabled ? (
          <IconButton
            onClick={handleClear}
            size="small"
            sx={{
              position: 'absolute',
              right: '30px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#AAAAAA',
              '&:hover': {
                color: DARK_GREY
              }
            }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Box>
      {error ? (
        <Box id={field.errorId} sx={sharedClasses.errorBox}>
          {error}
        </Box>
      ) : null}
    </FormControl>
  );
};

export default FormSelectField;
